/** Clip preview player (clip clock) with the masked lyric lines highlighted under the playhead. Used by Choose
 * (looping, autoplay unless ?agent=1) and the Clip page (paused, frame-stepping). Sound follows the remembered
 * Sound toggle (default on); an autoplay the browser refuses with sound plays muted until the first click. `onController` hands the
 * player to the caller (Choose's clip lanes seek it and follow its playhead); `overlay` is drawn over the video
 * (aria-hidden, no pointer events; the 16:9 frame in percent). */
import { useCallback, useEffect, type ReactNode } from "react";
import { fmtMs } from "../../lib/frames";
import type { FrameController } from "../player/controller";
import { PlayerStatus, Transport, VideoSurface, type Describe } from "../player/Player";
import { usePreviewSound } from "../player/sound";
import { useMediaInfo, usePlayer, usePlayerSelect } from "../player/usePlayer";
import type { ClipDetail } from "../types";

export function ClipPreview({ clip, loop, autoplay, keys = true, onController, overlay }: {
  clip: ClipDetail; loop: boolean; autoplay: boolean; keys?: boolean;
  onController?: (ctrl: FrameController | null) => void; overlay?: ReactNode;
}) {
  const mediaId = clip.preview_media_id ?? clip.master_media_id ?? null;
  const media = useMediaInfo(mediaId);
  const fps = media.data?.fps ?? 30;
  const { ctrl, videoRef } = usePlayer({ mediaKey: mediaId, fps, durationMs: clip.dur_ms, frameCount: media.data?.frames ?? null });
  useEffect(() => ctrl.setLoop({ inMs: null, outMs: null, on: loop }), [ctrl, loop]);
  useEffect(() => {
    if (!onController) return;
    onController(ctrl);
    return () => onController(null);
  }, [ctrl, onController]);
  usePreviewSound(ctrl);
  const ready = usePlayerSelect(ctrl, (s) => s.ready);
  useEffect(() => {
    if (autoplay && ready) ctrl.play({ mutedFallback: true });
  }, [autoplay, ready, ctrl]);
  const cur = usePlayerSelect(ctrl, (s) => clip.lines.findIndex((l) => l.t0_clip_ms <= s.tMs && s.tMs < l.t1_clip_ms));
  const describe: Describe = useCallback((t) => {
    const l = clip.lines.find((x) => x.t0_clip_ms <= t && t < x.t1_clip_ms);
    return { section: clip.label ?? null, line: l ? l.text_masked : null };
  }, [clip]);
  return (
    <div className="ws8-player-col">
      <div className="ws8-overlay-host">
        <VideoSurface ctrl={ctrl} videoRef={videoRef} mediaId={mediaId} webmId={clip.preview_media_id != null ? clip.preview_webm_media_id : null}
          label={`Clip #${clip.id} preview`}
          vcodec={media.data?.vcodec} />
        {overlay != null && mediaId != null && (
          <div className="ws8-video-overlay" aria-hidden="true" data-testid="preview-overlay">{overlay}</div>
        )}
      </div>
      <PlayerStatus ctrl={ctrl} describe={describe} />
      <Transport ctrl={ctrl} keys={keys} sound={mediaId != null} />
      {!media.data?.fps && mediaId != null && media.isSuccess && (
        <p className="ws8-warn" role="note">Frame rate unknown; assuming 30 fps.</p>
      )}
      {clip.lines.length > 0 && (
        <ol className="ws8-masked-lines" aria-label="Lyrics in this clip (masked)" data-testid="clip-lines">
          {clip.lines.map((l, i) => (
            <li key={i} aria-current={i === cur ? "true" : undefined}>
              <span className="ws8-muted">{fmtMs(l.t0_clip_ms)}</span> {l.text_masked}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
